import { Text, Title } from '@mantine/core'
import type { Patient, Reference } from '@medplum/fhirtypes'
import { ReferenceDisplay } from '@medplum/react'
import * as codeSystems from '@seen/core/codeSystems'
import {
  referenceDisplay,
  getDisplayString,
  getResourceDate,
  getResourceOwnerSortFilterValue,
} from '@seen/core/display'
import type { ActivityResource, SDR } from '@seen/core/models/tasks/sdr/sdr'
import { createReference } from '@seen/core/references'
import { generateUUID, isOfType, parseReferenceSafe } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { IconX } from '@tabler/icons-react'
import { first, orderBy } from 'lodash-es'
import { type JSX, useMemo } from 'react'
import { FhirQueryLoader } from '#src/components/core/FhirQueryLoader.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { ResourceCard } from '#src/components/core/ResourceCard.tsx'
import { DateDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import type { ColumnConfig } from '#src/components/fhir-table/FhirMRTTable.constants.tsx'
import { ReactTable } from '#src/components/fhir-table/ReactTable.tsx'
import { ResourceAutocomplete } from '#src/components/input/ResourceAutocomplete.tsx'
import { useFhirSearch } from '#src/hooks/useFhirSearch.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

interface SDRFulfillmentUpdatesTableProps {
  patientRef: Reference<Patient>
  sdr: SDR
}

export function SDRFulfillmentUpdatesTable({
  patientRef,
  sdr,
}: SDRFulfillmentUpdatesTableProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const columns = useMemo<ColumnConfig<ActivityResource>[]>(
    () => [
      {
        id: 'owner',
        headerText: 'Owner',
        render: (resource) => {
          switch (resource.resourceType) {
            case 'Appointment': {
              const owner = first(
                resource.participant?.filter(
                  (p) => parseReferenceSafe(p.actor)[0] === 'Practitioner'
                )
              )?.actor
              return owner ? (
                <ResourceCard value={owner} />
              ) : (
                <Text>No practitioner</Text>
              )
            }
            case 'MedicationRequest':
              return resource.requester ? (
                <ReferenceDisplay value={resource.requester} />
              ) : (
                <Text>No requester</Text>
              )
            case 'Composition': {
              const author = resource.author.find((a) =>
                isOfType(a, 'Practitioner')
              )

              return author ? (
                <ReferenceDisplay value={author} />
              ) : (
                <Text>No assessor</Text>
              )
            }
            default:
              return null
          }
        },
        valueType: 'text',
        getSortFilterValue: getResourceOwnerSortFilterValue,
        filteringType: 'text',
        enableSorting: true,
      },
      {
        id: 'description',
        headerText: 'Description',
        render: (r) => (
          <Link to={`/${r.resourceType}/${r.id}`}>
            <Text>{referenceDisplay(createReference(r))}</Text>
          </Link>
        ),
        valueType: 'text',
        getSortFilterValue: getDisplayString,
        filteringType: 'text',
        enableSorting: true,
      },
      {
        id: 'date',
        headerText: 'Added On',
        render: (r) => (
          <DateDisplay element={Text} value={getResourceDate(r)} />
        ),
        valueType: 'date',
        getSortFilterValue: (r) => new Date(getResourceDate(r)),
        filteringType: 'date-range',
        sortDescFirst: true,
        useAsInitialSort: true,
      },
    ],
    []
  )
  const appointmentInputIds = sdr.fulfillmentActivityReferences('Appointment')
  const medicationRequestInputIds =
    sdr.fulfillmentActivityReferences('MedicationRequest')
  const compositionInputIds = sdr.fulfillmentActivityReferences('Composition')

  // TODO: improve pattern around search
  // The following is a hack to ensure if there are no references, the query will return an empty array of results.
  // If an empty array is provided to the `_id` parameter, the query will return all results.
  const randomUUID = useMemo(() => generateUUID(), [])
  const appointmentQuery = useFhirSearch('Appointment', {
    _id: appointmentInputIds.length ? appointmentInputIds : [randomUUID],
  })
  const medicationRequestQuery = useFhirSearch('MedicationRequest', {
    _id: medicationRequestInputIds.length
      ? medicationRequestInputIds
      : [randomUUID],
  })
  const compositionQuery = useFhirSearch('Composition', {
    _id: compositionInputIds.length ? compositionInputIds : [randomUUID],
  })

  const patchMutation = usePatchMutation<SDR>()

  const mutateTaskAddInputReference = (
    resource: ActivityResource | undefined
  ): void => {
    if (!resource) {
      return
    }
    patchMutation.mutate({
      instance: sdr,
      mutateFn: (sdr) => {
        sdr.addFulfillmentActivity(resource)
      },
    })
  }

  const mutateTaskRemoveInputReference = (resource: ActivityResource): void => {
    patchMutation.mutate({
      instance: sdr,
      mutateFn: (sdr) => {
        sdr.removeFulfillmentActivity(resource)
      },
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Title order={4}>Fulfillment Updates</Title>
        <ResourceAutocomplete
          className="w-96"
          onOptionSubmit={mutateTaskAddInputReference}
          clearOnSubmit
          disabled={isReadOnly}
          placeholder="Add Activity"
          searchQuery={{
            Appointment: {
              patient: patientRef,
              _sort: '-_lastUpdated',
              _count: '100',
            },
            MedicationRequest: {
              patient: patientRef,
              _sort: '-_lastUpdated',
              _count: '100',
            },
            Composition: {
              patient: patientRef,
              _sort: '-_lastUpdated',
              _count: '100',
              type: `${codeSystems.ELATION_VISIT_NOTE_TEMPLATE_SYSTEM}|`,
            },
          }}
        />
      </div>
      <FhirQueryLoader
        query={[appointmentQuery, medicationRequestQuery, compositionQuery]}
      >
        {([appointments, medicationRequests, compositions]) => {
          const data = orderBy(
            [
              ...(appointments ?? []),
              ...(medicationRequests ?? []),
              ...(compositions ?? []),
            ],
            getResourceDate,
            'desc'
          )
          if (!data.length) {
            return null
          }
          return (
            <ReactTable<ActivityResource>
              columnConfigs={columns}
              data={data}
              tableOptions={{
                enableRowActions: true,
                positionActionsColumn: 'last',
                renderRowActions: ({ row }) => (
                  <Button
                    onClick={() => mutateTaskRemoveInputReference(row.original)}
                    variant="ghost"
                    size="xs"
                    disabled={isReadOnly}
                  >
                    <IconX className="size-4" />
                  </Button>
                ),
              }}
            />
          )
        }}
      </FhirQueryLoader>
    </div>
  )
}
